import { useState, useMemo, useEffect } from "react";
import ProductCard from "@/components/products/ProductCard";
import { Search, SlidersHorizontal, X, MapPin, Inbox } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocation as useAppLocation } from "@/contexts/LocationContext";
import { useStorefrontCategories } from "@/hooks/queries";
import { Skeleton } from "@/components/ui/skeleton";
import { useSearchParams } from "react-router-dom";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";

const Products = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get("category");
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string | null>(categoryParam);
  const [sortBy, setSortBy] = useState<string>("default");
  const [selectedStoreId, setSelectedStoreId] = useState<string | null>(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Price filtering states
  const [minPrice, setMinPrice] = useState<string>("");
  const [maxPrice, setMaxPrice] = useState<string>("");
  const [activePricePreset, setActivePricePreset] = useState<string>("all");

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;

  const { nearbyProducts, nearbyStores, selectedLocation, isLoadingProducts } = useAppLocation();
  const { data: categories = [], isLoading: isLoadingCategories } = useStorefrontCategories();

  // Sync category param from URL
  useEffect(() => {
    if (categoryParam !== undefined && categoryParam !== null) {
      setSelectedCategory(categoryParam);
    }
  }, [categoryParam]);

  // Reset pagination to page 1 on filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategory, selectedStoreId, searchQuery, sortBy, minPrice, maxPrice]);

  // Compute product counts for categories dynamically (based on all nearby products)
  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    nearbyProducts.forEach((p) => {
      if (p.category) {
        counts[p.category] = (counts[p.category] || 0) + 1;
      }
    });
    return counts;
  }, [nearbyProducts]);

  // Compute product counts for stores dynamically (based on all nearby products)
  const storeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    nearbyProducts.forEach((p) => {
      if (p.storeId) {
        counts[p.storeId] = (counts[p.storeId] || 0) + 1;
      }
    });
    return counts;
  }, [nearbyProducts]);

  // Dynamically compute the Top 5 Categories based on product counts
  const topCategories = useMemo(() => {
    return [...categories]
      .sort((a, b) => (categoryCounts[b.name] || 0) - (categoryCounts[a.name] || 0))
      .slice(0, 5);
  }, [categories, categoryCounts]);

  // Handle preset price ranges selection
  const handlePricePreset = (preset: string) => {
    setActivePricePreset(preset);
    if (preset === "all") {
      setMinPrice("");
      setMaxPrice("");
    } else if (preset === "under-100") {
      setMinPrice("");
      setMaxPrice("100");
    } else if (preset === "100-500") {
      setMinPrice("100");
      setMaxPrice("500");
    } else if (preset === "500-1000") {
      setMinPrice("500");
      setMaxPrice("1000");
    } else if (preset === "over-1000") {
      setMinPrice("1000");
      setMaxPrice("");
    }
  };

  // Handle custom range manual change
  const handleCustomPriceChange = (type: "min" | "max", value: string) => {
    setActivePricePreset("custom");
    if (type === "min") {
      setMinPrice(value);
    } else {
      setMaxPrice(value);
    }
  };

  // Clear all filters completely
  const clearAllFilters = () => {
    setSelectedCategory(null);
    setSelectedStoreId(null);
    setSearchQuery("");
    setMinPrice("");
    setMaxPrice("");
    setActivePricePreset("all");
    setSortBy("default");
  };

  // Apply filters and sorting
  const filteredProducts = useMemo(() => {
    let result = [...nearbyProducts];

    // Search query filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.description.toLowerCase().includes(query) ||
          p.storeName.toLowerCase().includes(query)
      );
    }

    // Category dropdown filter
    if (selectedCategory) {
      const categoryName = categories.find((c) => c.id === selectedCategory)?.name;
      if (categoryName) {
        result = result.filter((p) => p.category === categoryName);
      }
    }

    // Store dropdown filter
    if (selectedStoreId) {
      result = result.filter((p) => p.storeId === selectedStoreId);
    }

    // Price range filters
    const min = minPrice ? parseFloat(minPrice) : null;
    const max = maxPrice ? parseFloat(maxPrice) : null;

    if (min !== null && !isNaN(min)) {
      result = result.filter((p) => p.price >= min);
    }
    if (max !== null && !isNaN(max)) {
      result = result.filter((p) => p.price <= max);
    }

    // Sorting
    switch (sortBy) {
      case "price-low":
        result.sort((a, b) => a.price - b.price);
        break;
      case "price-high":
        result.sort((a, b) => b.price - a.price);
        break;
      case "rating":
        result.sort((a, b) => b.rating - a.rating);
        break;
      default:
        break;
    }

    return result;
  }, [searchQuery, selectedCategory, selectedStoreId, sortBy, minPrice, maxPrice, nearbyProducts, categories]);

  // Pagination logic
  const totalPages = Math.ceil(filteredProducts.length / itemsPerPage);

  const paginatedProducts = useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return filteredProducts.slice(start, start + itemsPerPage);
  }, [filteredProducts, currentPage]);

  const getPageNumbers = () => {
    const pages: (number | "ellipsis")[] = [];
    if (totalPages <= 5) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      if (currentPage <= 3) {
        pages.push(1, 2, 3, 4, "ellipsis", totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1, "ellipsis", totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push(1, "ellipsis", currentPage - 1, currentPage, currentPage + 1, "ellipsis", totalPages);
      }
    }
    return pages;
  };

  const openStoreCount = nearbyStores.filter((s) => s.isOpen).length;

  // Active filters helper names
  const activeCategoryName = categories.find((c) => c.id === selectedCategory)?.name;
  const activeStoreName = nearbyStores.find((s) => s.id === selectedStoreId)?.name;

  return (
    <div className="min-h-screen bg-background">
      {/* Header Banner */}
      <div className="bg-gradient-hero text-primary-foreground py-10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center gap-2 mb-2">
            <MapPin className="w-4 h-4 text-primary-foreground/80 animate-pulse" />
            <span className="text-sm text-primary-foreground/75 font-medium">
              Showing products from nearby open stores • {selectedLocation.label}
            </span>
          </div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold mb-2 tracking-tight">
            Explore Products
          </h1>
          <p className="text-primary-foreground/70 text-sm">
            {isLoadingProducts
              ? "Loading nearby products..."
              : `${nearbyProducts.length}+ products from ${openStoreCount} local stores near you`}
          </p>
        </div>
      </div>

      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8">
          
          {/* LEFT SIDEBAR - Price Filters & Top Categories */}
          <aside className="w-68 xl:w-72 shrink-0 hidden lg:block">
            <div className="sticky top-24 space-y-6">
              
              {/* Price Filter Panel */}
              <div className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
                <h3 className="font-heading font-semibold text-base mb-4 text-foreground flex items-center justify-between">
                  <span>Price Filter</span>
                  {(minPrice || maxPrice || activePricePreset !== "all") && (
                    <button
                      onClick={() => handlePricePreset("all")}
                      className="text-xs text-primary font-medium hover:underline transition-colors"
                    >
                      Clear
                    </button>
                  )}
                </h3>
                
                {/* Preset Ranges */}
                <div className="space-y-1.5 mb-4">
                  {[
                    { id: "all", label: "All Prices" },
                    { id: "under-100", label: "Under ₹100" },
                    { id: "100-500", label: "₹100 - ₹500" },
                    { id: "500-1000", label: "₹500 - ₹1000" },
                    { id: "over-1000", label: "Over ₹1000" },
                  ].map((preset) => (
                    <button
                      key={preset.id}
                      onClick={() => handlePricePreset(preset.id)}
                      className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                        activePricePreset === preset.id
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                      }`}
                    >
                      <span>{preset.label}</span>
                    </button>
                  ))}
                </div>

                {/* Custom Range Inputs */}
                <div className="pt-4 border-t border-border/50">
                  <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wider block mb-2">
                    Custom Range
                  </label>
                  <div className="flex items-center gap-2">
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                      <input
                        type="number"
                        placeholder="Min"
                        value={minPrice}
                        onChange={(e) => handleCustomPriceChange("min", e.target.value)}
                        className="w-full h-9 pl-6 pr-2 rounded-lg bg-secondary/30 border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                    <span className="text-muted-foreground text-xs">—</span>
                    <div className="relative flex-1">
                      <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                      <input
                        type="number"
                        placeholder="Max"
                        value={maxPrice}
                        onChange={(e) => handleCustomPriceChange("max", e.target.value)}
                        className="w-full h-9 pl-6 pr-2 rounded-lg bg-secondary/30 border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Top Categories Panel */}
              <div className="bg-card rounded-2xl border border-border/60 p-5 shadow-sm">
                <h3 className="font-heading font-semibold text-base mb-4 text-foreground">
                  Top Categories
                </h3>
                <div className="space-y-1">
                  {isLoadingCategories
                    ? Array.from({ length: 5 }).map((_, i) => (
                        <Skeleton key={i} className="h-9 w-full rounded-xl" />
                      ))
                    : topCategories.map((cat) => {
                        const count = categoryCounts[cat.name] || 0;
                        const isSelected = selectedCategory === cat.id;
                        return (
                          <button
                            key={cat.id}
                            onClick={() => setSelectedCategory(isSelected ? null : cat.id)}
                            className={`w-full flex items-center justify-between px-3 py-2.5 rounded-xl text-sm font-medium transition-all ${
                              isSelected
                                ? "bg-primary text-primary-foreground shadow-sm"
                                : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
                            }`}
                          >
                            <span className="flex items-center gap-2.5">
                              <span className="text-base">{cat.icon}</span>
                              <span className="truncate">{cat.name}</span>
                            </span>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                              isSelected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                            }`}>
                              {count}
                            </span>
                          </button>
                        );
                      })}
                </div>
              </div>

            </div>
          </aside>

          {/* MAIN PRODUCTS GRID & FILTERS PANEL */}
          <div className="flex-1 min-w-0">
            
            {/* Top Toolbar: Search Bar, Category select, Store select, Sort select */}
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              
              {/* Search Box */}
              <div className="relative flex-1">
                <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <input
                  type="text"
                  placeholder="Search products, stores, or brands..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full h-11 pl-10 pr-10 rounded-xl bg-card border border-border/80 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 transition-all shadow-sm"
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery("")}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>

              {/* Toolbar Controls */}
              <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 shrink-0">
                
                {/* Mobile Filters trigger (for Price Filter & Top Categories sidebar content) */}
                <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
                  <SheetTrigger asChild>
                    <Button
                      variant="outline"
                      className="lg:hidden flex items-center gap-2 h-11 px-4 rounded-xl border border-border/80 shadow-sm"
                    >
                      <SlidersHorizontal className="w-4 h-4" />
                      <span className="font-semibold text-sm">Price Filters</span>
                      {(minPrice || maxPrice || activePricePreset !== "all") && (
                        <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                      )}
                    </Button>
                  </SheetTrigger>
                  
                  <SheetContent side="left" className="w-[300px] sm:w-[360px] p-6 overflow-y-auto">
                    <SheetHeader className="mb-6">
                      <SheetTitle className="text-left font-heading font-bold text-xl flex items-center justify-between">
                        <span>Filters</span>
                        {(minPrice || maxPrice || activePricePreset !== "all") && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              handlePricePreset("all");
                              setMobileFiltersOpen(false);
                            }}
                            className="text-xs text-primary font-medium hover:bg-secondary h-8 px-2"
                          >
                            Clear Price
                          </Button>
                        )}
                      </SheetTitle>
                    </SheetHeader>

                    <div className="space-y-6">
                      {/* Price presets inside Sheet */}
                      <div className="space-y-3">
                        <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground">
                          Price Range
                        </h4>
                        <div className="space-y-1.5">
                          {[
                            { id: "all", label: "All Prices" },
                            { id: "under-100", label: "Under ₹100" },
                            { id: "100-500", label: "₹100 - ₹500" },
                            { id: "500-1000", label: "₹500 - ₹1000" },
                            { id: "over-1000", label: "Over ₹1000" },
                          ].map((preset) => (
                            <button
                              key={preset.id}
                              onClick={() => {
                                handlePricePreset(preset.id);
                                setMobileFiltersOpen(false);
                              }}
                              className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                activePricePreset === preset.id
                                  ? "bg-primary text-primary-foreground shadow-sm"
                                  : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                              }`}
                            >
                              <span>{preset.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>

                      {/* Custom Range inside Sheet */}
                      <div className="space-y-3 pt-4 border-t border-border/50">
                        <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground">
                          Custom Range
                        </h4>
                        <div className="flex items-center gap-2">
                          <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                            <input
                              type="number"
                              placeholder="Min"
                              value={minPrice}
                              onChange={(e) => handleCustomPriceChange("min", e.target.value)}
                              className="w-full h-9 pl-6 pr-2 rounded-lg bg-secondary/30 border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                            />
                          </div>
                          <span className="text-muted-foreground text-xs">—</span>
                          <div className="relative flex-1">
                            <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">₹</span>
                            <input
                              type="number"
                              placeholder="Max"
                              value={maxPrice}
                              onChange={(e) => handleCustomPriceChange("max", e.target.value)}
                              className="w-full h-9 pl-6 pr-2 rounded-lg bg-secondary/30 border border-border text-xs focus:outline-none focus:ring-1 focus:ring-primary/30"
                            />
                          </div>
                        </div>
                      </div>

                      {/* Top categories inside Sheet */}
                      <div className="space-y-3 pt-6 border-t border-border/50">
                        <h4 className="font-heading font-bold text-xs uppercase tracking-wider text-muted-foreground">
                          Top Categories
                        </h4>
                        <div className="space-y-1">
                          {topCategories.map((cat) => {
                            const count = categoryCounts[cat.name] || 0;
                            const isSelected = selectedCategory === cat.id;
                            return (
                              <button
                                key={cat.id}
                                onClick={() => {
                                  setSelectedCategory(isSelected ? null : cat.id);
                                  setMobileFiltersOpen(false);
                                }}
                                className={`w-full flex items-center justify-between px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                                  isSelected
                                    ? "bg-primary text-primary-foreground shadow-sm"
                                    : "text-muted-foreground hover:text-foreground hover:bg-secondary/50"
                                }`}
                              >
                                <span className="flex items-center gap-2">
                                  <span>{cat.icon}</span>
                                  <span>{cat.name}</span>
                                </span>
                                <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
                                  isSelected ? "bg-primary-foreground/20 text-primary-foreground" : "bg-muted text-muted-foreground"
                                }`}>
                                  {count}
                                </span>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    </div>
                  </SheetContent>
                </Sheet>

                {/* Category Dropdown Select */}
                <select
                  value={selectedCategory || ""}
                  onChange={(e) => setSelectedCategory(e.target.value || null)}
                  className="h-11 px-3 rounded-xl bg-card border border-border/80 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm font-medium cursor-pointer max-w-[150px] sm:max-w-none truncate"
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => {
                    const count = categoryCounts[cat.name] || 0;
                    return (
                      <option key={cat.id} value={cat.id}>
                        {cat.icon} {cat.name} ({count})
                      </option>
                    );
                  })}
                </select>

                {/* Store Dropdown Select */}
                <select
                  value={selectedStoreId || ""}
                  onChange={(e) => setSelectedStoreId(e.target.value || null)}
                  className="h-11 px-3 rounded-xl bg-card border border-border/80 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm font-medium cursor-pointer max-w-[150px] sm:max-w-none truncate"
                >
                  <option value="">All Stores</option>
                  {nearbyStores.filter(s => s.isOpen).map((store) => {
                    const count = storeCounts[store.id] || 0;
                    return (
                      <option key={store.id} value={store.id}>
                        🏪 {store.name} ({count})
                      </option>
                    );
                  })}
                </select>

                {/* Sort Option Dropdown Select */}
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="h-11 px-3 rounded-xl bg-card border border-border/80 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 shadow-sm font-medium cursor-pointer col-span-2 sm:col-span-1"
                >
                  <option value="default">Sort: Recommended</option>
                  <option value="price-low">Price: Low to High</option>
                  <option value="price-high">Price: High to Low</option>
                  <option value="rating">Rating: High to Low</option>
                </select>
              </div>

            </div>

            {/* Active Filters Badges */}
            {(selectedCategory || selectedStoreId || searchQuery || minPrice || maxPrice || activePricePreset !== "all") && (
              <div className="flex flex-wrap items-center gap-2 mb-6">
                <span className="text-xs text-muted-foreground font-semibold uppercase tracking-wider mr-1">
                  Active Filters:
                </span>
                {selectedCategory && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shadow-sm">
                    <span>Category: {activeCategoryName || "Selected"}</span>
                    <button
                      onClick={() => setSelectedCategory(null)}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {selectedStoreId && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shadow-sm">
                    <span>Store: {activeStoreName || "Selected"}</span>
                    <button
                      onClick={() => setSelectedStoreId(null)}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {searchQuery && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shadow-sm">
                    <span>Search: "{searchQuery}"</span>
                    <button
                      onClick={() => setSearchQuery("")}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {(minPrice || maxPrice || activePricePreset !== "all") && (
                  <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary border border-primary/20 shadow-sm">
                    <span>
                      Price:{" "}
                      {activePricePreset === "custom"
                        ? `₹${minPrice || 0} - ₹${maxPrice || "Max"}`
                        : activePricePreset === "under-100"
                        ? "Under ₹100"
                        : activePricePreset === "100-500"
                        ? "₹100 - ₹500"
                        : activePricePreset === "500-1000"
                        ? "₹500 - ₹1000"
                        : activePricePreset === "over-1000"
                        ? "Over ₹1000"
                        : "All"}
                    </span>
                    <button
                      onClick={() => handlePricePreset("all")}
                      className="hover:bg-primary/20 rounded-full p-0.5 transition-colors"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground font-semibold hover:underline underline-offset-2 ml-1"
                >
                  Clear All
                </button>
              </div>
            )}

            {/* Results Count Summary */}
            <div className="flex items-center justify-between mb-4">
              <p className="text-sm text-muted-foreground font-medium">
                Showing{" "}
                <span className="font-semibold text-foreground">
                  {filteredProducts.length === 0
                    ? 0
                    : `${(currentPage - 1) * itemsPerPage + 1} - ${Math.min(
                        currentPage * itemsPerPage,
                        filteredProducts.length
                      )}`}
                </span>{" "}
                of{" "}
                <span className="font-semibold text-foreground">
                  {filteredProducts.length}
                </span>{" "}
                product{filteredProducts.length !== 1 ? "s" : ""}
              </p>
            </div>

            {/* Products Grid Section */}
            {isLoadingProducts ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {Array.from({ length: 8 }).map((_, i) => (
                  <Skeleton key={i} className="aspect-square rounded-2xl" />
                ))}
              </div>
            ) : paginatedProducts.length > 0 ? (
              <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedProducts.map((product, i) => (
                  <ProductCard key={product.id} product={product} index={i} />
                ))}
              </div>
            ) : (
              <div className="text-center py-20 bg-card rounded-2xl border border-border/60 p-8 shadow-sm">
                <Inbox className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
                <p className="font-heading font-semibold text-lg">No products found</p>
                <p className="text-sm text-muted-foreground mt-1">
                  We couldn't find any products matching your active filters.
                </p>
                <Button
                  variant="outline"
                  className="mt-5 rounded-xl"
                  onClick={clearAllFilters}
                >
                  Reset All Filters
                </Button>
              </div>
            )}

            {/* ADS BANNER AT BOTTOM (WITH SMALL IMAGE) */}
            <div className="mt-12 bg-gradient-warm border border-border/60 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6 shadow-sm overflow-hidden relative">
              {/* Decorative glows */}
              <div className="absolute -top-12 -left-12 w-24 h-24 bg-primary/10 rounded-full blur-2xl" />
              <div className="absolute -bottom-12 -right-12 w-32 h-32 bg-accent/10 rounded-full blur-2xl" />
              
              <div className="flex-1 text-center md:text-left relative z-10">
                <span className="inline-block bg-accent/10 text-accent text-[10px] font-bold px-2.5 py-1 rounded-full mb-3 uppercase tracking-wider border border-accent/20">
                  Special Local Promo
                </span>
                <h3 className="font-heading font-bold text-xl md:text-2xl text-foreground mb-2">
                  Freshness Guaranteed from Local Stores!
                </h3>
                <p className="text-muted-foreground text-sm max-w-xl mb-4">
                  Order fresh fruits, vegetables, grocery, and daily essentials from trusted Kirana stores near you. Get up to <span className="font-semibold text-primary">30% OFF</span> on your first order.
                </p>
                <Button 
                  className="rounded-xl px-5 h-10 shadow-sm"
                  onClick={() => {
                    clearAllFilters();
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                >
                  Shop All Products
                </Button>
              </div>

              {/* Small Promo Image on Right */}
              <div className="w-28 h-28 md:w-32 md:h-32 shrink-0 rounded-2xl overflow-hidden shadow-md border border-border relative z-10 bg-white">
                <img
                  src="/images/promo-banner.png"
                  alt="Fresh groceries promo banner"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
              </div>
            </div>

            {/* PAGINATION CONTROLS */}
            {totalPages > 1 && (
              <div className="mt-10 flex justify-center border-t border-border/60 pt-6">
                <Pagination>
                  <PaginationContent>
                    <PaginationItem>
                      <PaginationPrevious
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage > 1) setCurrentPage(currentPage - 1);
                        }}
                        className={
                          currentPage === 1
                            ? "pointer-events-none opacity-50 select-none"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>

                    {getPageNumbers().map((page, idx) => (
                      <PaginationItem key={idx}>
                        {page === "ellipsis" ? (
                          <PaginationEllipsis />
                        ) : (
                          <PaginationLink
                            href="#"
                            isActive={currentPage === page}
                            onClick={(e) => {
                              e.preventDefault();
                              setCurrentPage(page);
                            }}
                            className="cursor-pointer"
                          >
                            {page}
                          </PaginationLink>
                        )}
                      </PaginationItem>
                    ))}

                    <PaginationItem>
                      <PaginationNext
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                        }}
                        className={
                          currentPage === totalPages
                            ? "pointer-events-none opacity-50 select-none"
                            : "cursor-pointer"
                        }
                      />
                    </PaginationItem>
                  </PaginationContent>
                </Pagination>
              </div>
            )}

          </div>

        </div>
      </div>
    </div>
  );
};

export default Products;
